import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import PredictionPaymentModal from '../components/PredictionPaymentModal';
import { toast } from 'sonner';
import {
  Sparkles, Flame, Snowflake, Clock, Target, TrendingUp, Shield,
  AlertTriangle, Loader2, ChevronDown, ChevronUp, BarChart3, Zap,
  ArrowLeft, RefreshCw, Star, Hash, Layers, Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const PosLabel = ['1ra', '2da', '3ra'];
const PosGradient = [
  'from-amber-400 to-orange-500',
  'from-green-400 to-emerald-500',
  'from-indigo-400 to-purple-500',
];

const LotteryCard = ({ analysis, aiTip, expanded, onToggle }) => {
  const conf = analysis.confidence || 0;
  const confColor = conf >= 75 ? 'text-green-400' : conf >= 55 ? 'text-amber-400' : 'text-red-400';

  const copyNumbers = () => {
    const nums = analysis.main_numbers.map(n => String(n.number).padStart(2, '0')).join('-');
    navigator.clipboard.writeText(nums);
    toast.success(`Copiado: ${nums}`);
  };

  return (
    <Card className="bg-slate-900/80 border-slate-700/50 overflow-hidden" data-testid={`aries-lottery-${analysis.lottery_id}`}>
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-800/30 transition-colors" onClick={onToggle}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-3 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: analysis.color || '#f59e0b' }} />
          <div className="min-w-0">
            <h3 className="text-white font-bold text-sm truncate">{analysis.lottery_name}</h3>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{analysis.draw_time}</span>
              <span>{analysis.draws_analyzed} sorteos</span>
              <span>{analysis.day_analysis}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Main Numbers */}
          <div className="flex gap-1.5">
            {analysis.main_numbers?.map((n, i) => (
              <div key={`main-${n.number}-${i}`} className="flex flex-col items-center gap-0.5">
                <span className="text-slate-600 text-[8px] font-bold">{PosLabel[i]}</span>
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${PosGradient[i]} flex items-center justify-center text-white font-black text-base shadow-lg`}>
                  {String(n.number).padStart(2, '0')}
                </div>
                <span className="text-[9px] font-mono text-slate-500">{n.score}</span>
              </div>
            ))}
          </div>

          {/* Confidence */}
          <div className="text-center min-w-[48px]">
            <p className={`text-xl font-black ${confColor}`}>{conf}%</p>
            <p className="text-slate-700 text-[8px]">CONF</p>
          </div>

          <button onClick={copyNumbers} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-600 hover:text-white transition-colors" title="Copiar">
            <Copy size={14} />
          </button>

          {expanded ? <ChevronUp size={16} className="text-slate-600" /> : <ChevronDown size={16} className="text-slate-600" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-800/60 pt-3 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* AI Insight */}
          {aiTip && (
            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 flex items-start gap-2">
              <Sparkles size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-300 text-xs">{aiTip.tip}</p>
                {aiTip.key_pattern && (
                  <p className="text-slate-500 text-[10px] mt-1">Patron: {aiTip.key_pattern}</p>
                )}
              </div>
            </div>
          )}

          {/* Main Numbers Detail */}
          <div className="grid grid-cols-3 gap-2">
            {analysis.main_numbers?.map((n, i) => (
              <div key={`detail-${n.number}-${i}`} className="p-3 rounded-lg bg-slate-800/40 border border-slate-700/30">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-2xl font-black bg-gradient-to-r ${PosGradient[i]} bg-clip-text text-transparent`}>
                    {String(n.number).padStart(2, '0')}
                  </span>
                  <span className="text-slate-600 text-[9px] font-bold">POS {n.position}</span>
                </div>
                <div className="space-y-1 text-[9px]">
                  {Object.entries(n.breakdown || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-slate-600">{k}</span>
                      <span className="text-slate-400 font-mono">{v}</span>
                    </div>
                  ))}
                  {n.overdue > 1.2 && (
                    <div className="flex justify-between text-amber-400">
                      <span>vencido</span>
                      <span className="font-mono">{n.overdue}x</span>
                    </div>
                  )}
                  <div className="flex gap-1 mt-1">
                    {n.pos_trending && <Badge className="text-[7px] px-1 py-0 bg-green-500/15 text-green-400 border-green-500/30">TREND</Badge>}
                    {n.is_mirror && <Badge className="text-[7px] px-1 py-0 bg-purple-500/15 text-purple-400 border-purple-500/30">ESPEJO</Badge>}
                    {n.is_repeat && <Badge className="text-[7px] px-1 py-0 bg-cyan-500/15 text-cyan-400 border-cyan-500/30">REPITE</Badge>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Alternatives + Combo Meta */}
          <div className="grid grid-cols-2 gap-2">
            {/* Alternatives */}
            <div className="p-2.5 rounded-lg bg-slate-800/30">
              <p className="text-slate-500 text-[10px] font-bold mb-1.5 flex items-center gap-1"><Target size={10} />ALTERNATIVAS</p>
              <div className="flex gap-2">
                {analysis.alternative_numbers?.map((a, i) => (
                  <div key={`alt-${a.number}-${i}`} className="text-center">
                    <span className="text-slate-600 text-[8px]">{PosLabel[i]}</span>
                    <div className="w-9 h-9 rounded-full bg-slate-700/40 border border-slate-600 flex items-center justify-center text-slate-300 font-bold text-sm">
                      {String(a.number).padStart(2, '0')}
                    </div>
                    <span className="text-[8px] text-slate-600">{a.score}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Combo Analysis */}
            <div className="p-2.5 rounded-lg bg-slate-800/30">
              <p className="text-slate-500 text-[10px] font-bold mb-1.5 flex items-center gap-1"><BarChart3 size={10} />COMBO</p>
              <div className="space-y-1 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-slate-600">Suma</span>
                  <span className="text-slate-400">{analysis.combo_meta?.sum} (rango: {analysis.combo_meta?.sum_range?.q25}-{analysis.combo_meta?.sum_range?.q75})</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Par/Impar</span>
                  <span className="text-slate-400">{analysis.combo_meta?.evens}P / {analysis.combo_meta?.odds}I</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Rangos</span>
                  <span className="text-slate-400">{analysis.combo_meta?.ranges?.join(' / ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Terminales</span>
                  <span className="text-slate-400">{analysis.combo_meta?.terminals?.join(', ')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Top Pales */}
          {analysis.top_pales?.length > 0 && (
            <div className="p-2.5 rounded-lg bg-slate-800/30">
              <p className="text-slate-500 text-[10px] font-bold mb-1.5 flex items-center gap-1"><Layers size={10} />TOP PALES</p>
              <div className="flex gap-2">
                {analysis.top_pales.map((p, i) => (
                  <div key={`pale-${p.pair?.[0]}-${p.pair?.[1]}`} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <span className="text-purple-300 font-bold text-sm">{String(p.pair[0]).padStart(2, '0')}</span>
                    <span className="text-purple-600">-</span>
                    <span className="text-purple-300 font-bold text-sm">{String(p.pair[1]).padStart(2, '0')}</span>
                    <span className="text-purple-600 text-[9px]">({p.count}x)</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Position Leaders + Hot/Cold + Terminals */}
          <div className="grid grid-cols-3 gap-2">
            {/* Position Leaders */}
            <div className="p-2.5 rounded-lg bg-slate-800/30">
              <p className="text-slate-500 text-[10px] font-bold mb-1">POR POSICION</p>
              {Object.entries(analysis.position_leaders || {}).map(([pos, nums]) => (
                <div key={pos} className="flex items-center gap-1 mb-0.5">
                  <span className="text-slate-600 text-[9px] w-6">{PosLabel[parseInt(pos) - 1]}</span>
                  <div className="flex gap-0.5">
                    {nums.slice(0, 3).map((n, i) => (
                      <span key={`n-${n.number}-${i}`} className="px-1 py-0 rounded bg-slate-700/30 text-slate-400 text-[9px] font-mono">{n.number}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Hot/Cold */}
            <div className="p-2.5 rounded-lg bg-slate-800/30">
              <p className="text-red-400 text-[10px] font-bold mb-1 flex items-center gap-1"><Flame size={10} />CALIENTES</p>
              <div className="flex flex-wrap gap-0.5">
                {analysis.hot_numbers?.slice(0, 5).map((h) => (
                  <span key={h.number} className="px-1 py-0 rounded bg-red-500/10 text-red-400 text-[9px] font-mono">{h.number}</span>
                ))}
              </div>
              <p className="text-blue-400 text-[10px] font-bold mt-1.5 mb-0.5 flex items-center gap-1"><Snowflake size={10} />FRIOS</p>
              <div className="flex flex-wrap gap-0.5">
                {analysis.cold_numbers?.slice(0, 5).map((c) => (
                  <span key={c.number} className="px-1 py-0 rounded bg-blue-500/10 text-blue-400 text-[9px] font-mono">{c.number}({c.overdue}x)</span>
                ))}
              </div>
            </div>

            {/* Terminals + Mirrors */}
            <div className="p-2.5 rounded-lg bg-slate-800/30">
              <p className="text-slate-500 text-[10px] font-bold mb-1 flex items-center gap-1"><Hash size={10} />TERMINALES</p>
              <div className="flex flex-wrap gap-0.5">
                {analysis.top_terminals?.map((t) => (
                  <span key={t.digit} className="px-1.5 py-0 rounded bg-cyan-500/10 text-cyan-400 text-[9px] font-mono">*{t.digit}({t.count})</span>
                ))}
              </div>
              {analysis.mirror_hits?.length > 0 && (
                <>
                  <p className="text-purple-400 text-[10px] font-bold mt-1.5 mb-0.5">ESPEJOS</p>
                  <div className="flex flex-wrap gap-0.5">
                    {analysis.mirror_hits.map((m) => (
                      <span key={m.number} className="px-1.5 py-0 rounded bg-purple-500/10 text-purple-400 text-[9px] font-mono">{m.number}({m.count})</span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Last Draw */}
          <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/20">
            <span className="text-slate-600 text-[10px]">Ultimo:</span>
            <div className="flex gap-1">
              {analysis.last_draw?.map((n, i) => (
                <span key={`ld-${n}-${i}`} className="px-1.5 py-0.5 rounded bg-slate-700/30 text-slate-500 text-[10px] font-mono">{String(n).padStart(2, '0')}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

const AriesPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [showPayment, setShowPayment] = useState(false);

  const generateAnalysis = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/aries/generate`, {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 45000 }
      );
      setResult(res.data);
      toast.success(`Analisis Aries: ${res.data.total_lotteries} loterias analizadas`);
      // Fetch AI insights in background (cached from server)
      setTimeout(async () => {
        try {
          const aiRes = await axios.get(`${API}/aries/ai-insights`, {
            headers: { Authorization: `Bearer ${token}` }, timeout: 35000
          });
          if (aiRes.data?.ai_insights) {
            setResult(prev => prev ? { ...prev, ai_insights: aiRes.data.ai_insights } : prev);
          }
        } catch (_) {}
      }, 8000);
    } catch (e) {
      console.error('Aries error:', e?.response?.status, e?.response?.data, e?.message);
      if (e.response?.status === 402) {
        setShowPayment(true);
      } else if (e.response?.status === 400) {
        toast.error(e.response?.data?.detail || 'No hay datos suficientes para el analisis');
      } else {
        toast.error(e.response?.data?.detail || 'Error generando analisis. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const findAiTip = (lotteryName) => {
    return result?.ai_insights?.insights?.find(t =>
      t.lottery?.includes(lotteryName?.split(' ')[0]) || lotteryName?.includes(t.lottery?.split(' ')[0])
    );
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-slate-500 hover:text-slate-300"><ArrowLeft size={18} /></Link>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Zap size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white tracking-tight" data-testid="aries-title">ARIES V2</h1>
              <p className="text-slate-600 text-xs">Analisis Posicional + Ciclos + Terminales + Espejos + Sócrates</p>
            </div>
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] ml-1">200 SORTEOS</Badge>
          </div>
        </div>

        {/* Generate */}
        {!result && !loading && (
          <div className="flex flex-col items-center justify-center py-16" data-testid="aries-generate-section">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 flex items-center justify-center mb-5">
              <Zap size={40} className="text-amber-400" />
            </div>
            <h2 className="text-white text-xl font-bold mb-2">Analisis Aries V2</h2>
            <p className="text-slate-500 text-sm text-center max-w-lg mb-1">
              Analiza los ultimos 200 sorteos por loteria con 7 capas de analisis:
              frecuencia ponderada, posicional, ciclos, dia de semana, transiciones, terminales y espejos.
            </p>
            <p className="text-slate-600 text-xs mb-6">Sócrates valida y refina cada seleccion</p>
            <Button
              onClick={generateAnalysis}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-bold px-8 py-5 rounded-xl shadow-lg"
              data-testid="aries-generate-btn"
            >
              <Sparkles size={20} className="mr-2" />
              Generar Analisis ($25)
            </Button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16" data-testid="aries-loading">
            <div className="relative mb-5">
              <div className="w-16 h-16 rounded-full border-3 border-amber-500/20 border-t-amber-500 animate-spin" />
              <Zap size={24} className="text-amber-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <h3 className="text-white text-lg font-bold mb-1">Analizando 200 sorteos por loteria...</h3>
            <p className="text-slate-500 text-sm">7 capas de analisis + Sócrates</p>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div className="space-y-4" data-testid="aries-results">
            {/* Summary bar */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-amber-400" />
                <span className="text-white font-semibold text-sm">{result.total_lotteries} loterias</span>
                <span className="text-slate-500 text-xs">{new Date(result.generated_at).toLocaleString('es-DO')}</span>
                {result.ai_insights && <Badge className="bg-green-500/15 text-green-400 border-green-500/30 text-[9px]">Sócrates Activo</Badge>}
              </div>
              <Button onClick={generateAnalysis} variant="outline" size="sm" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10" data-testid="aries-regenerate-btn">
                <RefreshCw size={14} className="mr-1" />
                Regenerar ($25)
              </Button>
            </div>

            {/* AI Strategy */}
            {result.ai_insights?.general_strategy && (
              <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/15 flex items-start gap-2" data-testid="aries-ai-strategy">
                <Star size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-200 text-xs leading-relaxed">{result.ai_insights.general_strategy}</p>
              </div>
            )}

            {/* Lottery Cards */}
            <div className="space-y-2">
              {result.analyses?.map((a, idx) => (
                <LotteryCard
                  key={a.lottery_id}
                  analysis={a}
                  aiTip={findAiTip(a.lottery_name)}
                  expanded={expandedIdx === idx}
                  onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                />
              ))}
            </div>

            {/* Disclaimer */}
            <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30 flex items-start gap-2">
              <Shield size={14} className="text-slate-700 flex-shrink-0 mt-0.5" />
              <p className="text-slate-700 text-[10px]">
                ARIES es analisis estadistico avanzado. Los resultados de loteria son aleatorios. Juega responsablemente.
              </p>
            </div>
          </div>
        )}
      </main>

      <PredictionPaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        onPaymentComplete={() => { setShowPayment(false); generateAnalysis(); }}
      />
    </div>
  );
};

export default AriesPage;
